import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Clock, Download, Filter, Pencil, Save, Trash2, X } from "lucide-react";

import {
  DECISION_ACTIONS,
  SPECIES,
  type DecisionAction,
  type DecisionDto,
} from "@shared/schema";
import { api } from "@/lib/api";
import { getDeviceId } from "@/lib/deviceId";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ACTION_META: Record<
  DecisionAction,
  {
    label: string;
    color: string;
    icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  }
> = {
  validate_now: { label: "Validate now", color: "hsl(var(--priority-high))", icon: Check },
  maybe_later: { label: "Maybe later", color: "hsl(var(--priority-mid))", icon: Clock },
  reject: { label: "Reject", color: "hsl(var(--destructive))", icon: X },
};

export function DecisionLogPage() {
  const qc = useQueryClient();
  const deviceId = getDeviceId();
  const [filter, setFilter] = useState<DecisionAction | "all">("all");

  const listQ = useQuery({
    queryKey: ["decisions", deviceId],
    queryFn: () => api.listDecisions({ deviceId }),
  });

  const rows = useMemo(() => {
    const data = listQ.data ?? [];
    return filter === "all" ? data : data.filter((d) => d.action === filter);
  }, [listQ.data, filter]);

  const grouped = useMemo(() => {
    const out: Record<string, DecisionDto[]> = {};
    for (const d of rows) {
      const key = d.queryRunId ?? "ungrouped";
      (out[key] ??= []).push(d);
    }
    return out;
  }, [rows]);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `academ-decisions-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["decisions"] });

  return (
    <div className="container py-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold">Decision Log</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every <em>Validate now</em> / <em>Maybe later</em> / <em>Reject</em> you recorded across
            runs. Stored on-device; v1 will migrate these to your authenticated workspace.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportJson} disabled={!rows.length}>
            <Download className="h-4 w-4" />
            Export JSON
          </Button>
        </div>
      </header>

      <section className="mt-5 flex items-center gap-2 text-sm">
        <span className="label-eyebrow inline-flex items-center gap-1 text-muted-foreground">
          <Filter className="h-3 w-3" /> Filter
        </span>
        <button
          type="button"
          onClick={() => setFilter("all")}
          className={filter === "all" ? "chip chip--active" : "chip"}
        >
          All ({listQ.data?.length ?? 0})
        </button>
        {DECISION_ACTIONS.map((a) => {
          const m = ACTION_META[a];
          const count = listQ.data?.filter((d) => d.action === a).length ?? 0;
          return (
            <button
              key={a}
              type="button"
              onClick={() => setFilter(a)}
              className={cn("chip", filter === a && "chip--active")}
              style={filter === a ? { borderColor: m.color, color: m.color } : undefined}
            >
              <m.icon className="h-3 w-3" style={{ color: m.color }} />
              {m.label} ({count})
            </button>
          );
        })}
      </section>

      {listQ.isPending && (
        <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
      )}

      {listQ.data && rows.length === 0 && (
        <div className="mt-6 rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          <p className="font-medium text-foreground">No decisions match this filter yet.</p>
          <p className="mt-1">
            Run a query, open any interaction, and click one of the decision buttons. They&rsquo;ll
            appear here automatically.
          </p>
        </div>
      )}

      <div className="mt-5 space-y-6">
        {Object.entries(grouped).map(([runId, items]) => (
          <section key={runId}>
            <div className="label-eyebrow mb-2">
              {runId === "ungrouped" ? "No run associated" : `Run ${runId.slice(0, 8)}`}
            </div>
            <ul className="divide-y divide-border rounded-lg border border-border bg-card text-sm">
              {items.map((d) => (
                <DecisionRow key={d.id} decision={d} onChange={invalidate} />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

function DecisionRow({
  decision,
  onChange,
}: {
  decision: DecisionDto;
  onChange: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState(decision.reason ?? "");
  const [action, setAction] = useState<DecisionAction>(decision.action);

  const updateMut = useMutation({
    mutationFn: () => api.updateDecision(decision.id, { action, reason: reason.trim() || null }),
    onSuccess: () => {
      setEditing(false);
      onChange();
    },
  });
  const deleteMut = useMutation({
    mutationFn: () => api.deleteDecision(decision.id),
    onSuccess: () => onChange(),
  });

  const meta = ACTION_META[decision.action];
  return (
    <li className="flex flex-wrap items-start gap-3 px-3 py-2">
      <span
        className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: meta.color }}
      />
      <div className="min-w-[180px] flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[13px] font-semibold">
            {decision.sourceGene} — {decision.partnerGene}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {SPECIES[decision.speciesKey].common}
          </span>
        </div>
        {editing ? (
          <div className="mt-2 space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {DECISION_ACTIONS.map((a) => {
                const m = ACTION_META[a];
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setAction(a)}
                    className={cn("chip text-[11px]", action === a && "chip--active")}
                    style={action === a ? { borderColor: m.color, color: m.color } : undefined}
                  >
                    <m.icon className="h-3 w-3" style={{ color: m.color }} />
                    {m.label}
                  </button>
                );
              })}
            </div>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              className="flex w-full resize-none rounded-md border border-input bg-background px-2.5 py-1.5 text-[12px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Reason for decision (optional)..."
            />
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => updateMut.mutate()} disabled={updateMut.isPending}>
                <Save className="h-3.5 w-3.5" /> Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-[11px] text-muted-foreground">
              <span className="font-semibold" style={{ color: meta.color }}>
                {meta.label}
              </span>
              {decision.reason ? ` · ${decision.reason}` : null}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {new Date(decision.createdAt).toLocaleString()}
              {decision.queryRunId && (
                <span className="ml-2 font-mono">run {decision.queryRunId.slice(0, 8)}</span>
              )}
            </div>
          </>
        )}
      </div>
      {!editing && (
        <div className="ml-auto flex items-center gap-1">
          <Button size="icon" variant="ghost" onClick={() => setEditing(true)} aria-label="Edit">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => {
              if (confirm("Delete this decision?")) deleteMut.mutate();
            }}
            aria-label="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </li>
  );
}
